import { utcToZonedTime } from 'date-fns-tz';
import { isEqualWith } from 'lodash-es';

import { Nullable } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { IRestaurantNode, OrderingStatus } from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ServiceMode } from 'state/service-mode/types';
import { IPreloadedStoreState } from 'utils/store-info';

import { DEFAULT_STORE_TIMEZONE, STATE_MAP } from '../constants';
import {
  IAvailabilityProps,
  IMergeRestaurantData,
  StorePermissions,
  curriedGetStoreStatus,
} from '../types';

import { getStoreLocalTime } from './store-time-utils';

export * from './store-time-utils';
export * from './store-geo-spatial-utils';
export * from './day-part-utils';

const isUSState = (value: string): value is keyof typeof STATE_MAP => value in STATE_MAP;

export const getUSState = (stateProvince: string): string =>
  isUSState(stateProvince) ? STATE_MAP[stateProvince] : stateProvince;

/**
 * Creates a function to get a store's status for store selection 1.0
 * TODO: This should be either deprecated in favor of `isRestaurantAvailable` in
 * use-service-mode-status/index.ts or added as an exported member of that hook as
 * an alternative to `isRestaurantAvailable`.
 */
export const curriedGetStoreStatusV1: curriedGetStoreStatus = ({
  store,
  serviceModeStatuses,
  selectedServiceMode,
}) => (serviceMode = selectedServiceMode) => {
  if (!store || !serviceMode) {
    return StorePermissions.NO_STORE_SELECTED;
  }

  const serviceModeStatus = serviceModeStatuses.find(sms => sms.serviceMode === serviceMode);
  const unavailableStoreSelected = store && !('hasMobileOrdering' in store);

  if (unavailableStoreSelected) {
    return StorePermissions.OPEN_AND_UNAVAILABLE;
  }

  return serviceModeStatus?.available && !serviceModeStatus.featureFlagDisabled
    ? StorePermissions.OPEN_AND_AVAILABLE
    : StorePermissions.CLOSED;
};

export const isAvailable = (rbiRestaurant?: IAvailabilityProps): boolean =>
  rbiRestaurant?.isAvailable ?? rbiRestaurant?.available ?? true;

/**
 * Merges the data from the GetRestaurant query and sanity query
 *
 * @param {IMergeRestaurantData} data
 * * rbiRestaurant - The restaurant returned from GetRestaurant
 * * sanityStore - the sanity document queried from groq
 *
 * @returns {IStore} merged store document
 *
 *
 * *NOTE* at the moment only the operating hours and timezone come from GetRestaurant
 */
export const mergeRestaurantData = ({
  rbiRestaurant,
  sanityStore,
}: IMergeRestaurantData): IRestaurant => {
  return {
    ...sanityStore,
    available: rbiRestaurant.available,
    deliveryHours: rbiRestaurant.deliveryHours || sanityStore.deliveryHours || null,
    diningRoomHours: rbiRestaurant.diningRoomHours || sanityStore.diningRoomHours || null,
    driveThruHours: rbiRestaurant.driveThruHours || sanityStore.driveThruHours || null,
    mobileOrderDriveThruHours:
      rbiRestaurant.mobileOrderDriveThruHours || sanityStore.mobileOrderDriveThruHours || null,
    timezone: rbiRestaurant.timezone,
    localTime: rbiRestaurant.localTime,
    serviceModeHours: rbiRestaurant.serviceModeHours,
    dayPartHours: rbiRestaurant.dayPartHours,
    isOneMenu: rbiRestaurant.isOneMenu,
  };
};

/**
 * Helper function to initialize store
 *
 * @param {IPreloadedStoreState} preloaded Preloaded store
 * @param {Nullable<ServiceMode>} serviceMode Selected service mode
 */
export const initStore = (
  preloaded: IPreloadedStoreState,
  serviceMode: Nullable<ServiceMode>
): IRestaurant | null => (!!preloaded.store && serviceMode ? preloaded.store : null);

/*
 * isMobileOrderingAvailable compares the app environment with the
 * restaurant environment returned from Sanity
 */
export const isMobileOrderingAvailable = (
  restaurant: IRestaurant,
  validMobileOrderingEnvs: string[] = []
) => {
  const { mobileOrderingStatus } = restaurant;

  if (!restaurant?.hasMobileOrdering || !mobileOrderingStatus) {
    return false;
  }
  if ([OrderingStatus.ALPHA, OrderingStatus.BETA].includes(mobileOrderingStatus)) {
    return false;
  }

  return validMobileOrderingEnvs.includes(mobileOrderingStatus);
};

export const isMobileOrderingAvailableExperiment = (
  restaurant: Pick<IRestaurant, 'mobileOrderingStatus' | 'hasMobileOrdering'>,
  validMobileOrderingEnvs: string[] = []
): boolean => {
  const { mobileOrderingStatus, hasMobileOrdering } = restaurant;

  if (!hasMobileOrdering || !mobileOrderingStatus) {
    return false;
  }
  if ([OrderingStatus.ALPHA, OrderingStatus.BETA].includes(mobileOrderingStatus)) {
    return false;
  }

  return validMobileOrderingEnvs.includes(mobileOrderingStatus);
};

export function logDeliveryStoreAssigned(restaurant: IRestaurantNode) {
  logRBIEvent({
    name: 'Delivery Store Assigned',
    type: EventTypes.Other,
    attributes: {
      restaurantId: restaurant._id ?? '',
      storeId: restaurant.storeId ?? '',
      restaurantAddress: restaurant.physicalAddress?.address1 ?? '',
      restaurantZip: restaurant.physicalAddress?.postalCode ?? '',
      restaurantCity: restaurant.physicalAddress?.city ?? '',
      restaurantState: restaurant.physicalAddress?.stateProvince ?? '',
      restaurantCountry: restaurant.physicalAddress?.country ?? '',
    },
  });
}

export function logDeliveryAddressSubmitted({
  storeId,
  failureReason,
  isNewAddress,
}: {
  storeId?: string | null;
  failureReason?: string;
  isNewAddress: boolean;
}) {
  logRBIEvent(
    {
      name: CustomEventNames.DELIVERY_ADDRESS_SUBMITTED,
      type: EventTypes.Other,
      attributes: {
        storeId: storeId ?? undefined,
        status: failureReason ? 'fail' : 'success',
        failureReason,
        searchType: isNewAddress ? 'newAddress' : 'savedAddress',
      },
    },
    { skipLoggingToBraze: true }
  );
}

export function getAvailableDayParts(store: IRestaurant): IDayPartBoundary[] | undefined {
  const dayPartHours = store.dayPartHours;
  const localTime = getStoreLocalTime();

  if (!localTime) {
    return;
  }

  return dayPartHours
    ?.filter(
      dayPartHour =>
        dayPartHour.open &&
        dayPartHour.close &&
        localTime >= dayPartHour.open &&
        localTime < dayPartHour.close
    )
    .map(
      dayPartHour =>
        ({
          key: dayPartHour.dayPart,
          startTime: utcToZonedTime(
            dayPartHour.open ?? '',
            store.timezone ?? DEFAULT_STORE_TIMEZONE
          ),
          endTime: utcToZonedTime(
            dayPartHour.close ?? '',
            store.timezone ?? DEFAULT_STORE_TIMEZONE
          ),
        } as IDayPartBoundary)
    );
}

export const isSameStoreAndServiceMode = (
  store: IRestaurant | null,
  newStore: IRestaurant,
  selectedServiceMode: ServiceMode | null,
  serviceMode: ServiceMode | null
) => {
  return (
    isEqualWith(store?.physicalAddress, newStore.physicalAddress) &&
    selectedServiceMode === serviceMode
  );
};

/*
  If MOBILE_ORDER_DRIVE_THRU is enabled and available, use it. If not, use DRIVE_THRU.
*/
export const getPreferredDriveThruServiceMode = (
  isMobileOrderDriveThruEnabledForStore: boolean,
  isServiceModeAvailable: (store: any, serviceMode: ServiceMode) => boolean,
  store: any
): ServiceMode => {
  if (
    isMobileOrderDriveThruEnabledForStore &&
    isServiceModeAvailable(store, ServiceMode.MOBILE_ORDER_DRIVE_THRU)
  ) {
    return ServiceMode.MOBILE_ORDER_DRIVE_THRU;
  }

  return ServiceMode.DRIVE_THRU;
};
